import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * One confirmation, worded the same everywhere in Accounting.
 *
 * Deletion here is genuinely irreversible and the records are financial, so
 * the dialog always names the thing being removed rather than asking "are you
 * sure?" about an unspecified row. `detail` is that name — a number, an
 * amount, a date — and `consequence` is for the cases where something else
 * changes as a result and the person should know before, not after.
 *
 * The server refuses anything unsafe on its own. This is the courtesy stop
 * before a request that will succeed.
 */
export default function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title = 'Delete this record?',
  detail = '',
  consequence = '',
  confirmLabel = 'Delete',
  busy = false,
  onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {detail && <span className="block text-[#062B1A]">{detail}</span>}
            <span className="mt-1 block">This cannot be undone.</span>
          </DialogDescription>
        </DialogHeader>
        {consequence && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
            {consequence}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Keep it
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="bg-red-700 text-white hover:bg-red-800"
          >
            {busy ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 size-4" />
            )}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
